import { useState } from 'react'
import { Feather, HelpCircle, X } from 'lucide-react'
import { useGame } from '../state/store'
import { fmt, Decimal } from '../engine/decimal'
import { getThemeForEdition } from '../config/themes'

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X']

const TIPS = [
  { title: 'Collecting Letters', text: 'Buy letters from the Shop. Each letter generates Ink passively once it reaches a high enough level.' },
  { title: 'Crafting Words', text: 'Use owned letters to spell words in Wordsmith. Each craft gives an Ink burst and levels up the word.' },
  { title: 'Heat & Weight', text: 'Vowels (E,A,O,I) refresh ink (+1 Heat) but press lightly (-1 Weight). Consonants press harder but dry ink. Balance them!' },
  { title: 'Mastery', text: 'Craft a word 10 times to Master it. Mastered words give a permanent global Ink bonus.' },
  { title: 'Word Sets', text: 'Complete themed sets (Animals, Colors, Elements) for a global bonus. Check Lexicon for set progress.' },
  { title: 'Editions', text: 'Unlock new Editions by progressing through the story. Each Edition adds new mechanics and themes.' },
]

export function Header() {
  const [showTips, setShowTips] = useState(false)
  const ink = useGame((s) => s.ink)
  const rate = useGame((s) => s.outputPerSec().toNumber())
  const edition = useGame((s) => s.edition)
  const type = useGame((s) => s.type)
  const theme = getThemeForEdition(edition)

  return (
    <header className="flex items-center justify-between border-b border-ink-border pt-6 pb-4">
      <div className="flex items-center gap-3">
        <Feather className="text-ink-teal" size={28} />
        <div>
          <h1 className="text-lg font-extrabold tracking-widest text-ink-teal">
            Incunabula
          </h1>
          <p className="text-[10px] uppercase tracking-[0.3em] text-ink-muted">
            Edition {ROMAN[edition] ?? edition} · {theme.name}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={() => setShowTips(true)}
          className="rounded-full border border-ink-border p-1.5 text-ink-muted hover:border-ink-tealdim hover:text-ink-teal transition"
          title="How to Play"
        >
          <HelpCircle size={20} />
        </button>
        <div className="text-right">
          <div className="text-2xl font-extrabold text-ink-text tabular-nums">
            {fmt(ink)} <span className="text-sm text-ink-muted">Ink</span>
          </div>
          <div className="text-xs text-ink-teal tabular-nums">
            {fmt(new Decimal(rate))} / s
            {type > 0 && (
              <span className="ml-2 text-amber-400">{type} Type</span>
            )}
          </div>
        </div>
      </div>

      {showTips && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowTips(false)}>
          <div className="mx-4 max-w-lg rounded-lg border border-ink-border bg-ink-panel p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-widest text-ink-teal">How to Play</h2>
              <button onClick={() => setShowTips(false)} className="text-ink-muted hover:text-ink-text">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              {TIPS.map((tip) => (
                <div key={tip.title}>
                  <h3 className="text-xs font-bold text-ink-text">{tip.title}</h3>
                  <p className="text-[11px] text-ink-muted leading-relaxed">{tip.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
